'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SPANISH_TRANSLATIONS: Record<string, string> = {};
const LANG_KEY = 'allywrk_language';

interface LanguageContextType {
  isSpanish: boolean;
  loading: boolean;
  toggle: () => void;
}

const LanguageContext = createContext<LanguageContextType>({
  isSpanish: false,
  loading: false,
  toggle: () => {},
});

export function useLanguage() {
  return useContext(LanguageContext);
}

function getAllTextNodes(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(textNode) {
      const parent = (textNode as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName.toLowerCase();
      if (['script', 'style', 'noscript', 'meta', 'link'].includes(tag)) return NodeFilter.FILTER_REJECT;
      if (parent.closest('[data-translate-ignore]')) return NodeFilter.FILTER_REJECT;
      if (!textNode.textContent?.trim()) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  const nodes: Text[] = [];
  let node;
  while ((node = walker.nextNode())) {
    nodes.push(node as Text);
  }
  return nodes;
}

async function translateText(text: string): Promise<string> {
  if (SPANISH_TRANSLATIONS[text]) return SPANISH_TRANSLATIONS[text];
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=es&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    const data = await res.json();
    const translated = data[0]?.map((item: [string]) => item[0]).join('') || text;
    SPANISH_TRANSLATIONS[text] = translated;
    return translated;
  } catch {
    return text;
  }
}

interface StoredNode {
  node: Text;
  original: string;
}

const storedNodes: StoredNode[] = [];

async function applyTranslation() {
  storedNodes.length = 0;
  const textNodes = getAllTextNodes(document.body);
  const uniqueTexts = [...new Set(textNodes.map(n => n.textContent!.trim()).filter(Boolean))];
  await Promise.all(uniqueTexts.map(t => translateText(t)));

  for (let node of textNodes) {
    const original = node.textContent!;
    const trimmed = original.trim();
    if (!trimmed) continue;
    const translated = SPANISH_TRANSLATIONS[trimmed];
    if (translated && translated !== trimmed) {
      storedNodes.push({ node, original });
      node.textContent = original.replace(trimmed, translated);
    }
  }
}

function restoreTranslation() {
  for (const { node, original } of storedNodes) {
    try {
      node.textContent = original;
    } catch {
      // node may have been removed from DOM
    }
  }
  storedNodes.length = 0;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [isSpanish, setIsSpanish] = useState(false);
  const [loading, setLoading] = useState(false);

  // On mount, check localStorage and auto-apply translation if needed
  useEffect(() => {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === 'es') {
      setIsSpanish(true);
      setLoading(true);
      applyTranslation().finally(() => setLoading(false));
    }
  }, []);

  // Re-apply translation on route changes (when DOM updates)
  useEffect(() => {
    if (!isSpanish) return;

    // Use a MutationObserver to detect when new page content is rendered
    const observer = new MutationObserver(() => {
      // Debounce to avoid re-translating mid-render
      clearTimeout((window as any).__translateTimer);
      (window as any).__translateTimer = setTimeout(() => {
        applyTranslation();
      }, 300);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      clearTimeout((window as any).__translateTimer);
    };
  }, [isSpanish]);

  const toggle = useCallback(async () => {
    if (loading) return;
    if (isSpanish) {
      restoreTranslation();
      setIsSpanish(false);
      localStorage.setItem(LANG_KEY, 'en');
    } else {
      setLoading(true);
      localStorage.setItem(LANG_KEY, 'es');
      await applyTranslation();
      setIsSpanish(true);
      setLoading(false);
    }
  }, [isSpanish, loading]);

  return (
    <LanguageContext.Provider value={{ isSpanish, loading, toggle }}>
      {children}
    </LanguageContext.Provider>
  );
}
